import React, { useState } from "react";
import { MapPin, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "bs_region_nudge_dismissed";
const REGIONS = [
  { key: "london", label: "London" },
  { key: "south_east", label: "South East" },
  { key: "midlands", label: "Midlands" },
  { key: "north", label: "North" },
  { key: "scotland", label: "Scotland" },
  { key: "wales", label: "Wales" },
  { key: "national", label: "UK national (no adjustment)" },
];

export const RegionNudge = () => {
  const { tenant, refreshMe, user } = useAuth();
  const [region, setRegion] = useState("london");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; } });

  const canWrite = user?.role === "owner" || user?.role === "admin";
  const show = canWrite && tenant && !tenant.region && !dismissed;
  if (!show) return null;

  const dismiss = () => { try { localStorage.setItem(DISMISS_KEY, "1"); } catch {} setDismissed(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/settings/region`, { region });
      await refreshMe();
      toast.success(region === "national" ? "Using UK national pricing." : "Region saved — benchmarks now match your area.");
      dismiss();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5" data-testid="region-nudge">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-600/10 flex items-center justify-center shrink-0">
          <MapPin className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-slate-900 text-sm">Set your region for local pricing</p>
          <p className="text-sm text-slate-600 mt-0.5">Pick your area and TradeOff adjusts rate benchmarks and AI pricing to match — e.g. London runs higher than the national average.</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
            <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="region-nudge-select" className="h-9 border border-slate-300 rounded-md text-sm px-2 bg-white sm:w-64">
              {REGIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <Button size="sm" onClick={save} disabled={busy} data-testid="region-nudge-save" className="bg-blue-600 hover:bg-blue-700 h-9">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <MapPin className="h-4 w-4 mr-1.5" />} Save region
            </Button>
          </div>
        </div>
        <button onClick={dismiss} data-testid="region-nudge-dismiss" className="text-slate-400 hover:text-slate-600 shrink-0" aria-label="Dismiss"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default RegionNudge;
